import { BufferAttribute, BufferGeometry, FileLoader, TypedArray } from "three";
import { StaticGeometryGenerator } from "three-mesh-bvh";
import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { IInitialisationReturnDataWithNextStep, IWorkerAnimationData, IWorkerAnimationUpdateTimeData, IWorkerAnimationUpdateTimeStepData, IWorkerData, IWorkerDataType, IWorkerGenerateGeometryData, IWorkerInitialisationData, IWorkerInitialisationWithNextStep, IWorkerMissingGeometryErrorData, IWorkerReturnData } from "./staticGeometry.worker";

type IBufferGeoCb = (g: BufferGeometry, id: string) => unknown;

export class StaticGeometryWorkerController {
    worker: Worker;
    name: string;
    gltfLoader: GLTFLoader;
    throttleGeometryGenerationToNthFrame = 1;
    private _busy = false;
    private _enabled = true;
    private _generalModelDataDict: {[url: string] : {
        staticGeometryGenerator?: StaticGeometryGenerator;
        onInitialisation?: () => unknown;
        onInitialisationCbArray?: (() => unknown)[];
        gltfPath: string;
        gltf: GLTF | null;
        arrayBuffer: ArrayBuffer | null;
        geometryIndex: BufferAttribute;
        positionAttribute: BufferAttribute;
        isInitialised?: boolean;
    }} = {}
    private _modelInstanceDataDict: {[id: string]: {
        delta: number;
        animationName: string;
        repetitions: number | undefined;
        clampWhenFinished: boolean;
        loop: boolean;
        bufferGeoCbs: IBufferGeoCb[];
        tempBufferGeom: BufferGeometry;
        frameCount: number;
    }} = {}

    private _taskQueue: IWorkerData[] = []

    constructor(args?: {throttle: number}) { 
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        this.worker = new (require(`worker-loader?inline=fallback&filename=[name].[contenthash].worker.js!./staticGeometry.worker`).default)()
        this.name = 'StaticGeometryWorker';
        this.throttleGeometryGenerationToNthFrame = args?.throttle ?? this.throttleGeometryGenerationToNthFrame;
        this.gltfLoader = new GLTFLoader();

        this.worker.onmessage = (e: MessageEvent<IWorkerReturnData>) => {
            // console.log('Return data: ', e.data.type, (e.data as any).id)
            if (!this._enabled) return;
            
            const { data } = e;

            if (data.type === IWorkerDataType.initialisation) {
                this._generalModelDataDict[data.url].isInitialised = true;
                for (let i = 0; i < (this._generalModelDataDict[data.url].onInitialisationCbArray ?? []).length; i++) {
                    this._generalModelDataDict[data.url].onInitialisationCbArray![i]();
                }
                this._busy = false;
                if ((data as IInitialisationReturnDataWithNextStep).forceNextStep === IWorkerDataType.generateStaticGeometry) {
                    this.generateStaticGeometry(data.url, (data as IInitialisationReturnDataWithNextStep).id);
                }
                return;
            }

            if (data.type === IWorkerDataType.updateAnimationData) {
                this._busy = false;
                return
            }
          
            // If the browser terminates the worker state from the initialisation required in the updates is lost and needs to be sent again.
            if (data.type === IWorkerDataType.error && (data.error === 'missing-state' || data.error === 'missing-generate-geometry-data') ) {
                const { url } = e.data;

                const workerData: IWorkerInitialisationData = {
                    type: IWorkerDataType.initialisation, 
                    arrayBuffer: this._generalModelDataDict[url].arrayBuffer!,
                    path: this._generalModelDataDict[url].gltfPath!,
                    url,
                }

                if (data.error === 'missing-generate-geometry-data') {
                    (workerData as IWorkerInitialisationWithNextStep).forceNextStep = IWorkerDataType.generateStaticGeometry;
                    (workerData as IWorkerInitialisationWithNextStep).id = (e.data as IWorkerMissingGeometryErrorData).id
                }

                if (this._busy) {
                    this._taskQueue.push(workerData);
                    return;
                }
                
                this._busy = true;
                this.worker.postMessage(workerData);
                console.warn('error missing state!')
                return;
            }  

            if (data.type === IWorkerDataType.bufferUpdate) {
                // if (!this._generalModelDataDict[data.url].positionAttribute) {
                    this._generalModelDataDict[data.url].positionAttribute = new BufferAttribute(data.attr as TypedArray, 3, false);
                // } else this._generalModelDataDict[data.url].positionAttribute.set(data.attr)

                this._modelInstanceDataDict[data.id].tempBufferGeom = this._modelInstanceDataDict[data.id].tempBufferGeom ?? new BufferGeometry()
                const bufferGeometrie = this._modelInstanceDataDict[data.id].tempBufferGeom;
                bufferGeometrie.setAttribute('position', this._generalModelDataDict[data.url].positionAttribute);

                if (!this._generalModelDataDict[data.url].geometryIndex && data.index) {
                    this._generalModelDataDict[data.url].geometryIndex = new BufferAttribute(data.index as TypedArray, 1, false);
                    bufferGeometrie.setIndex(this._generalModelDataDict[data.url].geometryIndex);
                }

                if (!this._modelInstanceDataDict[data.id].bufferGeoCbs) return;
                for (let i = 0; i < this._modelInstanceDataDict[data.id].bufferGeoCbs.length; i++) {
                    this._modelInstanceDataDict[data.id].bufferGeoCbs[i](bufferGeometrie, data.id); 
                }
                this._busy = false;
            }
        }
    }

    getModelArrayBuffer = (url: string): Promise<ArrayBuffer | null> => { 
        this._generalModelDataDict[url].gltfPath = url.substr(0, url.lastIndexOf('/') + 1);
        
        const arrayBufferLoader = new FileLoader().setResponseType('arraybuffer');
        return new Promise<ArrayBuffer>((resolve, reject) => {
            arrayBufferLoader.load(url, arrayBuffer => {
                if (typeof arrayBuffer !== 'string') resolve(arrayBuffer);
                else reject('string returned from array buffer loader')
            }, undefined, 
            (error) => {
                reject(error)
            })
        })
    }

    setAnimationName = (name: string, id: string) => {
        this._modelInstanceDataDict[id].animationName = name;
    }

    reset = (id: string, url: string) => {
        if (this._modelInstanceDataDict[id]) this._modelInstanceDataDict[id].delta = 0; 
        this.setWorkerAnimationTime(0, id, url)
    }

    tick = () => {
        if (this._taskQueue.length) {
            this._busy = true;
            this.worker.postMessage(this._taskQueue[0]);
            this._taskQueue.shift();
        }
    }

    updateAnimationTimeByMissedTimeStep = (time: number, id: string) => {
        this._modelInstanceDataDict[id].delta = this._modelInstanceDataDict[id].delta ?? 0;
        this._modelInstanceDataDict[id].delta += time;
    }

    initialiseWorkerWithGLTFArrayBuffer = (arrayBuffer: ArrayBuffer, url: string) => {
        const gltfPath = this.setUrlPath(url);
        this._generalModelDataDict[url].arrayBuffer = arrayBuffer;
        if (!this._enabled) return;
        // Send model array buffer to worker to generate second gltf model there
        if (!this._generalModelDataDict[url].arrayBuffer && !arrayBuffer) throw('Could not initialise worker: missing arraybuffer')
        this._busy = true;
        this.worker.postMessage({ type: IWorkerDataType.initialisation, url, arrayBuffer: arrayBuffer ?? this._generalModelDataDict[url].arrayBuffer, path: gltfPath } as IWorkerInitialisationData);
    }

    createInitialiseWorkerWithGLTFArrayBufferPromise = (arrayBuffer: ArrayBuffer, url: string) => {
        const initialisationPromise = new Promise<boolean>(res => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (!this._generalModelDataDict[url]) this._generalModelDataDict[url] = {} as any;
            this.onInitialised(() => res(true), url);
        })
        this.initialiseWorkerWithGLTFArrayBuffer(arrayBuffer, url);
        return initialisationPromise;
    }

    onInitialised = (cb: () => unknown, url: string) => {
        if (!this._generalModelDataDict[url]?.onInitialisationCbArray) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (!this._generalModelDataDict[url]) this._generalModelDataDict[url] = {} as any;
            this._generalModelDataDict[url].onInitialisationCbArray = [];
        }
        this._generalModelDataDict[url].onInitialisationCbArray?.push(cb);
    }

    setUrlPath = (url: string) => {
        this._generalModelDataDict[url].gltfPath = url.substr(0, url.lastIndexOf('/') + 1);
        return this._generalModelDataDict[url].gltfPath;
    }

    isModelInitialised = (url: string) => {
        return !!this._generalModelDataDict[url]?.isInitialised;
    }

    updateWorkerAnimationByTimeStep = (timeStep: number, id: string, url: string,useQueue = false) => {
        if (!this._enabled) return;

        if (!this._modelInstanceDataDict[id].animationName) console.warn('Could not update worker. Missing animation name')
           
        this._modelInstanceDataDict[id].frameCount = this._modelInstanceDataDict[id].frameCount ?? 0;
        this._modelInstanceDataDict[id].frameCount++;
        if (this._modelInstanceDataDict[id]?.frameCount % this.throttleGeometryGenerationToNthFrame !== 0) {
            this._modelInstanceDataDict[id].delta = this._modelInstanceDataDict[id].delta ?? 0;
            this._modelInstanceDataDict[id].delta += timeStep;
            return
        }

        const newTimeStep = timeStep + (this._modelInstanceDataDict[id].delta ?? 0)

        let workerData = this._taskQueue.filter(d => d.type === IWorkerDataType.updateByTimeStep && d.id === id)[0] as unknown as IWorkerAnimationUpdateTimeStepData | undefined;
       
        if (workerData) {
            workerData.timeStep += newTimeStep;
            workerData.name = this._modelInstanceDataDict[id].animationName
        } else {
            workerData = {
                type: IWorkerDataType.updateByTimeStep, 
                timeStep: newTimeStep,
                name: this._modelInstanceDataDict[id].animationName,
                url,
                id
            }
            if (useQueue) {
                this._modelInstanceDataDict[id].delta = 0;
                this._taskQueue.push(workerData);
            } 
        }

        if (!this._busy && !useQueue) {
            this._busy = true;
            this._modelInstanceDataDict[id].delta = 0;
            this.worker.postMessage(workerData);
        } 
    }

    setWorkerAnimationTime = (time: number, id: string, url: string, useQueue = true) => {
        if (!this._enabled) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!this._modelInstanceDataDict[id]) this._modelInstanceDataDict[id] = {} as any;
        if (!this._modelInstanceDataDict[id].animationName) {
            console.warn('Could not update worker. Missing animation name');
            return;
        }

        const workerData: IWorkerAnimationUpdateTimeData = {
            type: IWorkerDataType.updateTime, 
            time,
            name: this._modelInstanceDataDict[id].animationName,
            url,
            id
        } 

        if (useQueue) {
            this._taskQueue.push(workerData);
            return;
        }

        if (!this._busy) {
            this._busy = true;
            this.worker.postMessage(workerData);
        }
    }

    generateStaticGeometry = (url: string, id: string) => {
        const workerData: IWorkerGenerateGeometryData = {
            type: IWorkerDataType.generateStaticGeometry,
            url,
            id
        }
        this.worker.postMessage(workerData);
    }

    setAnimationData = (a: {loop: boolean, repetitions?: number | undefined, clampWhenFinished: boolean, name: string, url: string, id: string, useQueue?: boolean}) => {
        const {loop, repetitions, clampWhenFinished, name, url, id, useQueue = true} = a;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!this._modelInstanceDataDict[id]) this._modelInstanceDataDict[id] = {} as any;
        this._modelInstanceDataDict[id].loop = loop;
        this._modelInstanceDataDict[id].repetitions = repetitions;
        this._modelInstanceDataDict[id].clampWhenFinished = clampWhenFinished;
        this._modelInstanceDataDict[id].animationName = name;

        const workerData: IWorkerAnimationData = {
            type: IWorkerDataType.updateAnimationData,
            clampWhenFinished,
            repetitions,
            loop,
            name,
            url,
            id
        }

        if (useQueue) {
            this._taskQueue.push(workerData);
            return;
        }
        if (!this._busy) {
            this._busy = true;
            this.worker.postMessage(workerData);
        }
    }

    getActiveAnimationName = (id: string) => {
        return this._modelInstanceDataDict[id].animationName
    }

    onStaticGeometryChange = (id: string, cb: IBufferGeoCb) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (! this._modelInstanceDataDict[id]) this._modelInstanceDataDict[id] = {} as any;
        this._modelInstanceDataDict[id].bufferGeoCbs = [];
        this._modelInstanceDataDict[id].bufferGeoCbs.push(cb);
    }
    
    public get busy() : boolean {
        return this._busy
    }

}

